import { Document, Page, PDFViewer, Text, View } from "@react-pdf/renderer"

import global from "global"
import { pdfStyles } from "pages/ocr"
import { useEffect, useState } from "react"
import { toast } from "react-toastify"
import request from "request"
import { useSelector } from "store/hooks"
import { calculateRisk } from "utils/calculateRisk"
import { getRiskLabel } from "utils/getRiskLabel"
import { normalizeText } from "utils/normalizeText"
import { useClientContext } from ".."
import ResultBox from "../components/result-box"

const Result = () => {
  const { group } = useClientContext()
  const user = useSelector((state) => state.user)
  const [macroData, setMacroData] = useState<global.microData>()

  useEffect(() => {
    if (user.role === "expert") return

    request
      .get("/admin/constant_values")
      .then((res) => setMacroData(res.data))
      .catch((error) => {
        const errorMessage =
          error?.response?.data?.message ||
          error.message ||
          "An unknown error occurred."
        toast.error(`Error fetching constant values: ${errorMessage}`)
      })
  }, [user])

  if (!group.scoring_approved) {
    return <ResultBox />
  }

  const renderClientData = (app: global.application, index: number) => {
    const risk = group.applications.find((e) => e.id === app.id)?.risk_log
      ?.risks

    const riskObject = group.applications.find((e) => e.id == app.id)?.risk_log

    const riskValue = Number(risk?.total_risk)
    const riskText = calculateRisk(isNaN(riskValue) ? 0 : riskValue)

    return (
      <Page style={pdfStyles.page} key={app.id} size={"A4"}>
        <View style={pdfStyles.heading}>
          <Text>
            Client {index + 1}
            {": "}
            <Text
              style={
                app.scoring_log?.is_approved
                  ? pdfStyles.approved
                  : pdfStyles.rejected
              }
            >
              {app.scoring_log?.is_approved ? "Approved" : "Rejected"}
            </Text>
          </Text>
        </View>
        <View style={pdfStyles.heading3}>
          <Text>
            {app.scoring_log?.prediction &&
              `Prediction: ${app.scoring_log?.prediction}%`}
          </Text>

          <Text>
            {`Risk factor: ${riskObject?.is_approved ? "Approved" : "Rejected"} (${riskObject?.risks?.high_risk_count ?? 0})`}
          </Text>
        </View>
        <View style={pdfStyles.section}>
          <View style={pdfStyles.table}>
            {row2("Customer ID", app.customerId ?? "")}
            {row2("Application ID", app.applicationId ?? "")}
            {row2("ID", app.id ?? "")}
            {row2("Gender", app.scoring_log?.gender ?? "")}
            {row2("Family Status", app.scoring_log?.familyStatus ?? "")}
            {row2("Dependents", app.scoring_log?.dependents)}
            {row2("Employment Sphere", app.scoring_log?.employmentSphere)}
            {row2("Employment Level", app.scoring_log?.employmentLevel)}
            {row2("Work Experience", app.scoring_log?.workExperience)}
            {row2("Co-Borrower", app.scoring_log?.coBorrower)}
            {row2("Monthly Income", `${app.scoring_log?.monthlyIncome} JOD`)}

            {row2("Age", `${app.scoring_log?.age} years`)}
            {row2(
              "Total personal expenses (monthly)",
              app.scoring_log?.total_personal_expenses_monthly ?? "",
            )}
            {row2(
              "Active Ahli Bank monthly payment",
              app.scoring_log?.active_ahli_bank_monthly_payment ?? "",
            )}
            {row2(
              "Requested loan’s monthly payment",
              Number(app.scoring_log?.requested_loan_monthly_payment).toFixed(
                2,
              ) ?? "",
            )}
            {row2(
              "Sum of selected monthly payments",
              app.scoring_log?.sum_of_selected_payments ?? "",
            )}
            {row2(
              "Selected monthly payments",
              app.scoring_log?.selected_monthly_payments
                ? Object.entries(app.scoring_log.selected_monthly_payments)
                    .map(([key, value]) => {
                      let labelKey = key
                        .replace(/_/g, " ")
                        .replace(
                          /\b(loan|credit card)\b/i,
                          (match) =>
                            match.charAt(0).toUpperCase() + match.slice(1),
                        )

                      const match = key.match(/\d+$/)
                      if (match) {
                        labelKey = labelKey.replace(/\d+$/, `#${match[0]}`)
                      }

                      return `${labelKey} — ${value}`
                    })
                    .join("\n")
                : "",
            )}

            {row2(
              "Over Indebtedness Ratio",
              `${Math.round(Number(app.scoring_log?.over_independence_ratio) * 100)}%`,
            )}

            <Text style={pdfStyles.title}>Product data</Text>
            <View style={pdfStyles.table}>
              {app.product === "Housing" && (
                <View style={pdfStyles.tr}>
                  <View style={pdfStyles.td}>
                    <Text>Loan type</Text>
                  </View>
                  <View style={pdfStyles.td}>
                    <Text>{app.scoring_log?.loan_type}</Text>
                  </View>
                </View>
              )}
              {(app.product === "Housing" ||
                app.product === "Land purchase" ||
                app.product === "Mortgage" ||
                app.product === "Auto") && (
                <View style={pdfStyles.tr}>
                  <View style={pdfStyles.td}>
                    <Text>Property value</Text>
                  </View>
                  <View style={pdfStyles.td}>
                    <Text>{app.scoring_log?.property_value}</Text>
                  </View>
                </View>
              )}
              {(app.product === "Housing" || app.product === "Auto") && (
                <View style={pdfStyles.tr}>
                  <View style={pdfStyles.td}>
                    <Text>Property age at loan maturity</Text>
                  </View>
                  <View style={pdfStyles.td}>
                    <Text>{app.scoring_log?.property_age_at_end}</Text>
                  </View>
                </View>
              )}
              {(app.product === "Housing" ||
                app.product === "Land purchase" ||
                app.product === "Mortgage") && (
                <View style={pdfStyles.tr}>
                  <View style={pdfStyles.td}>
                    <Text>Property outside boundaries of regulation</Text>
                  </View>
                  <View style={pdfStyles.td}>
                    <Text>{app.scoring_log?.property_outside_regulations}</Text>
                  </View>
                </View>
              )}
              {app.product === "Auto" && (
                <View style={pdfStyles.tr}>
                  <View style={pdfStyles.td}>
                    <Text>Car evaluation score</Text>
                  </View>
                  <View style={pdfStyles.td}>
                    <Text>{app.scoring_log?.car_evaluation_score}</Text>
                  </View>
                </View>
              )}
              {app.product === "Credit Card" && (
                <View style={pdfStyles.tr}>
                  <View style={pdfStyles.td}>
                    <Text>Credit card limit</Text>
                  </View>
                  <View style={pdfStyles.td}>
                    <Text>{app.scoring_log?.credit_card_limit}</Text>
                  </View>
                </View>
              )}
              {app.product === "Personal" && (
                <>
                  <View style={pdfStyles.tr}>
                    <View style={pdfStyles.td}>
                      <Text>Target market list</Text>
                    </View>
                    <View style={pdfStyles.td}>
                      <Text>{app.scoring_log?.target_market_list}</Text>
                    </View>
                  </View>
                  <View style={pdfStyles.tr}>
                    <View style={pdfStyles.td}>
                      <Text>Length of service (in months)</Text>
                    </View>
                    <View style={pdfStyles.td}>
                      <Text>{app.scoring_log?.length_of_service}</Text>
                    </View>
                  </View>
                  <View style={pdfStyles.tr}>
                    <View style={pdfStyles.td}>
                      <Text>Against collateral</Text>
                    </View>
                    <View style={pdfStyles.td}>
                      <Text>{app.scoring_log?.collateral}</Text>
                    </View>
                  </View>
                </>
              )}
              {(app.product === "Housing" ||
                app.product === "Land purchase" ||
                app.product === "Mortgage" ||
                app.product === "Auto") && (
                <View style={pdfStyles.tr}>
                  <View style={pdfStyles.td}>
                    <Text>LTV</Text>
                  </View>
                  <View style={pdfStyles.td}>
                    <Text>
                      {Number(app.scoring_log?.ltv * 100)?.toFixed(2)}%
                    </Text>
                  </View>
                </View>
              )}
              <View style={pdfStyles.tr}>
                <View style={pdfStyles.td}>
                  <Text>DBR</Text>
                </View>
                <View style={pdfStyles.td}>
                  <Text>{Number(app.scoring_log?.dbr * 100).toFixed(2)}%</Text>
                </View>
              </View>
              <View style={pdfStyles.tr}>
                <View style={pdfStyles.td}>
                  <Text>Stop factors</Text>
                </View>
                <View style={pdfStyles.td}>
                  <Text>
                    {app.scoring_log?.stop_factors_is_approved
                      ? "Approved"
                      : "Rejected"}
                  </Text>
                </View>
              </View>
            </View>

            <Text style={pdfStyles.title}>Verification</Text>
            <View style={pdfStyles.table}>
              <View style={pdfStyles.tr}>
                <View style={pdfStyles.td}>
                  <Text>CBJ Black list / Returned Cheques</Text>
                </View>
                <View style={pdfStyles.td}>
                  <Text>{app.scoring_log?.cbj_black_list}</Text>
                </View>
              </View>
              <View style={pdfStyles.tr}>
                <View style={pdfStyles.td}>
                  <Text>Internal black list</Text>
                </View>
                <View style={pdfStyles.td}>
                  <Text>{app.scoring_log?.internal_black_list}</Text>
                </View>
              </View>
              <View style={pdfStyles.tr}>
                <View style={pdfStyles.td}>
                  <Text>Inspector / Financial legal cases</Text>
                </View>
                <View style={pdfStyles.td}>
                  <Text>{app.scoring_log?.inspector_legal_cases}</Text>
                </View>
              </View>
            </View>

            <Text style={pdfStyles.title}>Risks</Text>
            <View style={pdfStyles.table}>
              {risk &&
                Object.entries(risk)
                  .sort(
                    ([keyA], [keyB]) =>
                      (keyA === "high_risk_count" ? 1 : 0) -
                      (keyB === "high_risk_count" ? 1 : 0),
                  )
                  .map(([key, value]) => (
                    <View key={key} style={pdfStyles.tr}>
                      <View style={pdfStyles.td}>
                        <Text>{normalizeText(key)}</Text>
                      </View>
                      <View style={pdfStyles.td}>
                        <Text>{getRiskLabel(String(value))}</Text>
                      </View>
                    </View>
                  ))}
            </View>
          </View>
        </View>
      </Page>
    )
  }

  return (
    <>
      <PDFViewer style={{ width: "100%", height: "calc(100vh - 150px)" }}>
        <Document>
          <Page style={pdfStyles.page} size={"A4"}>
            <View style={pdfStyles.heading}>
              <Text>
                Result:{" "}
                <Text
                  style={
                    group.is_approved_group === "approved"
                      ? pdfStyles.approved
                      : pdfStyles.rejected
                  }
                >
                  {group.is_approved_group === "approved"
                    ? "Approved"
                    : "Rejected"}
                </Text>
              </Text>
            </View>
            <View style={pdfStyles.heading3}>
              <Text>
                {group.prediction &&
                  `Prediction: ${group.prediction.toFixed(2)}%`}
              </Text>

              <Text>{`Risk: ${group.risk_approved}(${group?.risk_count_summary ?? 0})`}</Text>
            </View>
            <View>
              <Text style={pdfStyles.text}>
                Date/Time: {new Date(group.created_at ?? "").toLocaleString()}
              </Text>
              <Text style={pdfStyles.text}>Group ID: {group.id}</Text>
            </View>
            <View style={pdfStyles.section}>
              <Text style={pdfStyles.title}>Credit info</Text>
              <View style={pdfStyles.table}>
                {row2(
                  "Amount",
                  `${group.applications[0].loanAmount.toFixed(2)} JOD`,
                )}
                {row2(
                  "Duration",
                  `${group.applications[0]?.scoring_log?.loanDuration} mon.`,
                )}
                {row2(
                  "Interest rate (annual)",
                  `${group.applications[0].interestRate}%`,
                )}
                {row2("Number of clients", group.applications.length)}
                {row2(
                  "Branch",
                  group.applications[0].scoring_log?.branch ?? "",
                  true,
                )}
                {row2(
                  "Threshold",
                  `${Number(group.applications[0].scoring_log?.threshold) * 100}%`,
                )}
                {row2(
                  "Over Indebtedness Ratio Threshold",
                  `${Number(macroData?.hard_oir_threshold) * 100}%`,
                )}
                {row2(
                  "DBR Threshold",
                  `${(Number(macroData?.dbr_threshold) * 100)?.toFixed(2)}%`,
                )}
              </View>
            </View>
          </Page>
          {group.applications.map((e, i) => renderClientData(e, i))}
        </Document>
      </PDFViewer>
      <ResultBox />
    </>
  )
}

const row2 = (key: any, value: any, arabic?: boolean) => (
  <View style={pdfStyles.tr}>
    <View style={pdfStyles.td}>
      <Text>{key}</Text>
    </View>
    <View style={pdfStyles.td}>
      <Text style={arabic ? pdfStyles.arabic : {}}>{String(value)}</Text>
    </View>
  </View>
)

export default Result
